/* eslint-disable react-refresh/only-export-components */

import { Incident } from "@/types/incidents";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { useIncidents } from "./IncidentsContext";
import { useSelectedAlert } from "./SelectedAlertContext";
import { useAuth } from "./AuthContext";

interface AlertsPanelContextType {
  isAlertsPanelOpen: boolean;
  isMainChatOpen: boolean;
  setIsAlertsPanelOpen: (open: boolean) => void;
  setIsMainChatOpen: (open: boolean) => void;
  currentAlert: Incident | null;
  setCurrentAlert: (alert: Incident | null) => void;
  relocateToAlert: Incident | null;
  setRelocateToAlert: (alert: Incident | null) => void;
}

const AlertsPanelContext = createContext<AlertsPanelContextType | undefined>(
  undefined
);

export const AlertsPanelProvider = ({ children }: { children: ReactNode }) => {
  const [isAlertsPanelOpen, setIsAlertsPanelOpen] = useState(false);
  const [isMainChatOpen, setIsMainChatOpen] = useState(false);
  const [currentAlert, setCurrentAlert] = useState<Incident | null>(null);
  const [relocateToAlert, setRelocateToAlert] = useState<Incident | null>(null);
  const location = useLocation();
  const { incidents, fetchIncidents } = useIncidents();
  const { setSelectedAlertId, selectedAlertId } = useSelectedAlert();
  const { isAuthenticated } = useAuth();
  const initialLoadRef = useRef(true);

  useEffect(() => {
    if (incidents.length === 0 && initialLoadRef.current && isAuthenticated) {
      fetchIncidents();
      initialLoadRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidents.length]);

  useEffect(() => {
    if (!selectedAlertId) {
      setRelocateToAlert(null)
    }
  }, [selectedAlertId])

  useEffect(() => {
    const handleRoute = async () => {
      const isAlertsRoute = location.pathname.startsWith("/alerts");
      const chatMatch = location.pathname.match(/\/alerts\/chat\/(\d+)/);
      const chatAlertId = chatMatch ? Number.parseInt(chatMatch[1], 10) : null;
      const isMapRoute = location.pathname === "/map";

      if (isAlertsRoute) {
        setIsAlertsPanelOpen(true);

        if (chatMatch && chatAlertId) {
          setIsMainChatOpen(true);

          const matchingAlert = incidents.find(
            (a) => a.incidentID === chatAlertId
          );
          if (matchingAlert) {
            setIsMainChatOpen(true);
            setCurrentAlert(matchingAlert);
            setSelectedAlertId(chatAlertId);
          } else {
            setIsMainChatOpen(false);
            setCurrentAlert(null);
            setSelectedAlertId(null);
          }
        } else {
          setIsMainChatOpen(false);
        }
      } else {

        if (!chatMatch) {
          setIsAlertsPanelOpen(false);
          setIsMainChatOpen(false);
          setCurrentAlert(null);
        }
        if (isMapRoute) {
          setSelectedAlertId(null);
        }
      }
    };

    handleRoute();
  }, [location.pathname, incidents, setSelectedAlertId, currentAlert]);

  useEffect(() => {
    if (currentAlert) {
      const updatedAlert = incidents.find(
        (a) => a.incidentID === currentAlert.incidentID
      );
      if (
        updatedAlert &&
        JSON.stringify(updatedAlert) !== JSON.stringify(currentAlert)
      ) {
        setCurrentAlert(updatedAlert);
      }
    }
  }, [incidents, currentAlert]);

  const contextValue = useMemo(() => ({
    isAlertsPanelOpen,
    setIsAlertsPanelOpen,
    isMainChatOpen,
    setIsMainChatOpen,
    currentAlert,
    setCurrentAlert,
    relocateToAlert,
    setRelocateToAlert
  }), [isAlertsPanelOpen, isMainChatOpen, currentAlert, relocateToAlert]);

  return (
    <AlertsPanelContext.Provider value={contextValue}>
      {children}
    </AlertsPanelContext.Provider>
  );
};

export const useAlertsPanel = () => {
  const context = useContext(AlertsPanelContext);
  if (context === undefined) {
    throw new Error("useAlertsPanel must be used within AlertsPanelProvider");
  }
  return context;
};
